import { supportedCountries } from './CountryUtils';
import { SUPPORTED_LOCALES } from './supportedLocales';

const URL_LOCALE_PATTERN = /^[a-z]{2}_[a-z]{2}$/;

/**
 * All locale codes that may legitimately appear in the URL path
 * (/:country/:locale/…). Union of the translation-bundle locales — which
 * intentionally also cover countries with existing users that are no
 * longer offered for registration (e.g. at/nl/ru/lt) — and the runtime
 * `supportedCountries` (registration-only combinations such as tr_it,
 * plus the English variant of every supported country).
 */
const validUrlLocales = new Set<string>(SUPPORTED_LOCALES);
for (const { countryCode, languageCode } of supportedCountries) {
  validUrlLocales.add(`${languageCode}_${countryCode}`);
  validUrlLocales.add(`en_${countryCode}`);
}

/**
 * Check whether a /:country/:locale/ URL prefix is a valid combination:
 * the locale must be a known locale whose country part matches the
 * country segment (e.g. /de/en_de is valid, /de/en_jp is not).
 * Tolerates dashed and upper-case URL variants (en-de, EN_DE).
 */
export const isValidUrlCountryLocale = (
  country: string,
  locale: string,
): boolean => {
  const normalizedLocale = locale.replace(/-/g, '_').toLowerCase();

  return (
    URL_LOCALE_PATTERN.test(normalizedLocale) &&
    normalizedLocale.endsWith(`_${country.toLowerCase()}`) &&
    validUrlLocales.has(normalizedLocale)
  );
};
